import type {
  FormFieldResponse,
  InspectionDeviceEntryResponse,
  InspectionDeviceHistoryEntryResponse,
  InspectionOfflineFieldSetResponse,
  InspectionResponse,
  InspectionScopeDeviceResponse,
  SelectOption,
} from 'src/api/ProactApiClient';
import {clientQuery} from 'src/redux/clientQuery';
import type {ClientQueryResult} from 'src/redux/clientQuery';
import {isEffectivelyOffline} from 'src/offline/connectivity';
import {offlineDb} from 'src/offline/db';
import {offlineQuery, readSnapshotData} from 'src/offline/offlineQuery';
import {getReadySession, isOwnedSession} from 'src/offline/offlineSessionManager';
import type {OfflineSession} from 'src/offline/types';
import type {LocalEntryState} from './entryComposition';
import {composeDeviceEntry} from './entryComposition';
import {
  inspectionContextKey,
  inspectionSnapshotKeys,
  isInspectionContextKey,
  parseInspectionContextKey,
} from './snapshotKeys';

/** The ready session bound to this inspection — the client-side version pin. */
export const boundReadyInspectionSession = (
  inspectionId: number
): Promise<OfflineSession | undefined> =>
  getReadySession(inspectionContextKey(inspectionId));

/** All ready inspection-bound sessions — for reads keyed only by device/type. */
const activeReadyInspectionSessions = async (): Promise<OfflineSession[]> => {
  try {
    const sessions = await offlineDb.sessions
      .where('status')
      .equals('active')
      .toArray();
    return sessions.filter(
      session =>
        session.ready &&
        isInspectionContextKey(session.contextKey) &&
        isOwnedSession(session)
    );
  } catch {
    return [];
  }
};

const readDeviceEntryFromSnapshot = async (
  session: OfflineSession,
  inspectionId: number,
  deviceId: number
): Promise<InspectionDeviceEntryResponse | undefined> => {
  const devices = await readSnapshotData<InspectionScopeDeviceResponse[]>(
    session.id,
    inspectionSnapshotKeys.devices(inspectionId)
  );
  const device = devices?.find(current => current.deviceId === deviceId);
  if (!device) return undefined;

  const fieldSets = await readSnapshotData<InspectionOfflineFieldSetResponse[]>(
    session.id,
    inspectionSnapshotKeys.definitions(inspectionId)
  );
  const fieldSet = fieldSets?.find(
    set => set.equipmentTypeId === device.equipmentTypeId
  );

  const local = await readSnapshotData<LocalEntryState>(
    session.id,
    inspectionSnapshotKeys.entry(inspectionId, deviceId)
  );

  return composeDeviceEntry(device, fieldSet, local);
};

// Reads keyed by inspection id serve one snapshot from that inspection's bound
// session.
const inspectionSnapshotQueryFn = <T>(
  key: (inspectionId: number) => string,
  call: (inspectionId: number) => Promise<T>
) =>
  offlineQuery<number, T>({
    contextKey: inspectionContextKey,
    read: (session, inspectionId) =>
      readSnapshotData<T>(session.id, key(inspectionId)),
    call,
  });

export const inspectionMetaQueryFn = <T>(call: (id: number) => Promise<T>) =>
  inspectionSnapshotQueryFn(inspectionSnapshotKeys.meta, call);

export const inspectionScopeDevicesQueryFn = (
  call: (id: number) => Promise<InspectionScopeDeviceResponse[]>
) => inspectionSnapshotQueryFn(inspectionSnapshotKeys.devices, call);

export interface InspectionCustomDeviceOptions {
  sites: SelectOption[];
  equipmentTypes: SelectOption[];
}

// The bundle's field sets are built from the scoped devices' distinct equipment
// types, so the :devices snapshot is the same legal set — and it carries names.
const distinctEquipmentTypeOptions = (
  devices: InspectionScopeDeviceResponse[]
): SelectOption[] => {
  const seen = new Map<number, SelectOption>();
  for (const device of devices) {
    if (device.equipmentTypeId > 0 && !seen.has(device.equipmentTypeId)) {
      seen.set(device.equipmentTypeId, {
        id: device.equipmentTypeId,
        label: device.equipmentTypeName,
      });
    }
  }
  return Array.from(seen.values());
};

// Custom-device picker options scoped to one inspection. Offline, the legal
// equipment types + the pinned site come from the bundle, not live endpoints.
export const inspectionCustomDeviceOptionsQueryFn = (
  call: (inspectionId: number) => Promise<InspectionCustomDeviceOptions>
) =>
  offlineQuery<number, InspectionCustomDeviceOptions>({
    contextKey: inspectionContextKey,
    read: async (session, inspectionId) => {
      const meta = await readSnapshotData<InspectionResponse>(
        session.id,
        inspectionSnapshotKeys.meta(inspectionId)
      );
      const devices = await readSnapshotData<InspectionScopeDeviceResponse[]>(
        session.id,
        inspectionSnapshotKeys.devices(inspectionId)
      );
      if (!meta || !devices) return undefined;
      return {
        sites: [{id: meta.siteId, label: meta.siteName}],
        equipmentTypes: distinctEquipmentTypeOptions(devices),
      };
    },
    call,
  });

export interface InspectionDeviceEntryArg {
  inspectionId: number;
  deviceId: number;
}

export const inspectionDeviceEntryQueryFn = (
  call: (args: InspectionDeviceEntryArg) => Promise<InspectionDeviceEntryResponse>
) =>
  offlineQuery<InspectionDeviceEntryArg, InspectionDeviceEntryResponse>({
    contextKey: args => inspectionContextKey(args.inspectionId),
    read: (session, args) =>
      readDeviceEntryFromSnapshot(session, args.inspectionId, args.deviceId),
    call,
  });

export interface InspectionDeviceHistoryArg {
  deviceId: number;
  /** Set by the record flow to pin history to that inspection's session; other
   * callers omit it and hit the network unless the device is offline. */
  inspectionId?: number;
}

// Without a named inspection, an offline caller scans every bound session for
// the device before the network is tried.
const scanHistoryOffline = async (
  deviceId: number
): Promise<InspectionDeviceHistoryEntryResponse[] | undefined> => {
  if (!isEffectivelyOffline()) return undefined;
  for (const session of await activeReadyInspectionSessions()) {
    const inspectionId = parseInspectionContextKey(session.contextKey)!;
    const history = await readSnapshotData<
      InspectionDeviceHistoryEntryResponse[]
    >(session.id, inspectionSnapshotKeys.history(inspectionId, deviceId));
    if (history) return history;
  }
  return undefined;
};

export const inspectionDeviceHistoryQueryFn = (
  call: (deviceId: number) => Promise<InspectionDeviceHistoryEntryResponse[]>
) =>
  offlineQuery<InspectionDeviceHistoryArg, InspectionDeviceHistoryEntryResponse[]>({
    contextKey: arg =>
      arg.inspectionId === undefined
        ? undefined
        : inspectionContextKey(arg.inspectionId),
    read: (session, arg) =>
      readSnapshotData(
        session.id,
        inspectionSnapshotKeys.history(arg.inspectionId!, arg.deviceId)
      ),
    call: async arg =>
      (await scanHistoryOffline(arg.deviceId)) ?? call(arg.deviceId),
  });

// No caller passes inspection context (admin field editors only), so only the
// offline fallback scan diverts from the network.
export const equipmentTypeFieldsQueryFn =
  (call: (equipmentTypeId: number) => Promise<FormFieldResponse[]>) =>
  async (equipmentTypeId: number): Promise<ClientQueryResult<FormFieldResponse[]>> => {
    if (isEffectivelyOffline()) {
      for (const session of await activeReadyInspectionSessions()) {
        const inspectionId = parseInspectionContextKey(session.contextKey)!;
        const fieldSets = await readSnapshotData<
          InspectionOfflineFieldSetResponse[]
        >(session.id, inspectionSnapshotKeys.definitions(inspectionId));
        const fieldSet = fieldSets?.find(
          set => set.equipmentTypeId === equipmentTypeId
        );
        if (fieldSet) return {data: fieldSet.fields};
      }
    }
    return clientQuery(call)(equipmentTypeId);
  };
